import React, { useEffect, useState } from "react";
import { IUserDetails } from "../../exports/interfaces";
import useMobile from "../../hooks/useMobile";
import { useParams } from "react-router-dom";
import Select from "react-select";
import {
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import { Upload } from "lucide-react";
import { useOption } from "../../context/OptionContext";
import {
  renderExistingFilePreview,
  renderFilePreview,
  validateFileSize,
} from "../../exports/functions";
import { useLoader } from "../../context/LoaderContext";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { useRole } from "../../context/RoleContext";
import { isValidEmail } from "../register/registerValidation";
import { useUser } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";

const SelectWithError = Select as unknown as React.ComponentType<any>;

const EditUser = ({ userData }: { userData: IUserDetails[] }) => {
  const { id } = useParams();
  const { setLoading } = useLoader();
  const { statusOptions } = useOption();
  const { user } = useUser();
  const { sendNotification } = useNotification();
  const { roles } = useRole();
  const isMobile = useMobile();
  const [userInfo, setUserInfo] = useState<IUserDetails>({
    userName: "",
    mobileNumber: "",
    email: "",
    citizenship: "",

    companyName: "",
    department: "",
    position: "",

    userRole: "",
    userStatus: "",
    fileName: "",
    file: null,
    documentUrl: null,
  });
  const [errors, setErrors] = useState({
    userName: "",
    mobileNumber: "",
    email: "",
    citizenship: "",
    companyName: "",
    department: "",
    position: "",
    userRole: "",
    userStatus: "",
  });

  useEffect(() => {
    if (!id) return;

    const selectedUser = userData.find((item) => item.id === Number(id));

    if (selectedUser) {
      setUserInfo(selectedUser);
    }
  }, [id, userData]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!validateFileSize(file)) {
        e.target.value = "";
        return;
      }
      setUserInfo((prev) => ({
        ...prev,
        fileName: file.name,
        file: file,
      }));
    }
  };
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    const updatedValue =
      name === "citizenship" ? value.replace(/\D/g, "").slice(0, 14) : value;

    setUserInfo((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));
    // setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const getUserErrors = (data: IUserDetails) => {
    const validationErrors = {
      userName: "",
      mobileNumber: "",
      email: "",
      citizenship: "",
      companyName: "",
      department: "",
      position: "",
      userRole: "",
      userStatus: "",
    };

    if (!data.userName?.trim()) {
      validationErrors.userName = "User Name is required.";
    }

    if (!data.mobileNumber?.trim()) {
      validationErrors.mobileNumber = "Mobile Number is required.";
    } else if (!/^\d{10}$/.test(data.mobileNumber.trim())) {
      validationErrors.mobileNumber = "Exactly 10 digits required.";
    }

    if (!data.email?.trim()) {
      validationErrors.email = "Email Address is required.";
    } else if (!isValidEmail(data.email)) {
      validationErrors.email = "Invalid Email format.";
    }

    if (!data.citizenship?.trim()) {
      validationErrors.citizenship = "Citizenship Number is required.";
    } else if (!/^\d{14}$/.test(data.citizenship.trim())) {
      validationErrors.citizenship = "Exactly 14 digits required.";
    }

    if (!data.companyName?.trim()) {
      validationErrors.companyName = "Company Name is required.";
    }

    if (!data.department?.trim()) {
      validationErrors.department = "Department is required.";
    }

    if (!data.position?.trim()) {
      validationErrors.position = "Position is required.";
    }

    if (!data.userRole?.trim()) {
      validationErrors.userRole = "User Role is required.";
    }

    if (!data.userStatus?.trim()) {
      validationErrors.userStatus = "User Status is required.";
    }

    return validationErrors;
  };

  const scrollToFirstError = (validationErrors: Record<string, string>) => {
    const fieldOrder = [
      "userName",
      "mobileNumber",
      "email",
      "citizenship",
      "companyName",
      "department",
      "position",
      "userRole",
      "userStatus",
    ];

    const firstErrorField = fieldOrder.find((field) => validationErrors[field]);

    if (!firstErrorField) return;

    const element =
      firstErrorField === "position" ||
      firstErrorField === "userRole" ||
      firstErrorField === "userStatus"
        ? (document.querySelector(
            `[data-field="${firstErrorField}"]`,
          ) as HTMLElement | null)
        : (document.querySelector(
            `[name="${firstErrorField}"]`,
          ) as HTMLElement | null);

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      // element.focus?.();
    }
  };

  const validateUserFields = (data: IUserDetails) => {
    const validationErrors = getUserErrors(data);
    setErrors(validationErrors);

    if (Object.values(validationErrors).some((error) => error !== "")) {
      scrollToFirstError(validationErrors);
      return false;
    }

    return true;
  };
  const positionOptions = [
    { value: "CEO", label: "CEO" },
    { value: "Chief Financial Officer", label: "Chief Financial Officer" },
    {
      value: "Chief Administration Officer",
      label: "Chief Administration Officer",
    },
    { value: "Registration Officer", label: "Registration Officer" },
    { value: "Manager", label: "Manager" },
    { value: "IT Officer", label: "IT Officer" },
  ];

  const userRoleOptions =
    roles?.map((role) => ({
      value: role.label,
      label: role.label,
    })) || [];

  const userStatusOptions =
    statusOptions
      ?.find((item) => item.name === "User Statuses")
      ?.statusOptions?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const handleSelectChange = (
    name: keyof IUserDetails,
    selected: SelectOption | null,
  ) => {
    setUserInfo((prev) => ({
      ...prev,
      [name]: selected?.value || "",
    }));
    // setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    if (!id || !userInfo) return;

    if (!validateUserFields(userInfo)) {
      return;
    }

    setLoading(true);

    try {
      const payload = new FormData();

      payload.append("userName", userInfo.userName || "");
      payload.append("mobileNumber", userInfo.mobileNumber || "");
      payload.append("email", userInfo.email || "");
      payload.append("citizenship", userInfo.citizenship || "");

      payload.append("companyName", userInfo.companyName || "");
      payload.append("department", userInfo.department || "");
      payload.append("position", userInfo.position || "");

      payload.append("userRole", userInfo.userRole || "");
      payload.append("userStatus", userInfo.userStatus || "");

      if (userInfo.file) {
        payload.append("documentUrl", userInfo.file);
      }

      const response = await axiosClient.put(`/users/${id}`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200 || response.status === 201) {
        sendNotification(`User updated`, user?.user?.userName || "User");
        dynamicSuccess("User updated successfully!");
      }
    } catch (error: any) {
      const responseMessage = error?.response?.data?.message;

      const message = Array.isArray(responseMessage)
        ? responseMessage[0]
        : responseMessage || "Something went wrong";

      dynamicFailed(message);
    } finally {
      setTimeout(() => setLoading(false), 800);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>Edit User</h3>
          <small>Edit a User</small>
        </div>
      </div>
      <div className={`dashboard-card`}>
        <div className="row g-3">
          <div className="col-12">
            <div className="dashboard-card">
              <h5 className="text-uppercase mb-4">USER INFORMATION</h5>
              <div className="row g-4">
                <div className="col-md-6">
                  <label className="form-label">
                    User Name <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.userName ? "input-error" : ""}`}
                    placeholder="Enter User Name"
                    name="userName"
                    value={userInfo.userName}
                    onChange={handleChange}
                  />
                  {errors.userName && (
                    <p className="error-message">{errors.userName}</p>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Mobile Number <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.mobileNumber ? "input-error" : ""}`}
                    placeholder="Enter Mobile Number"
                    name="mobileNumber"
                    value={userInfo.mobileNumber}
                    onChange={(e) => {
                      if (/^[0-9]*$/.test(e.target.value)) {
                        handleChange(e);
                      }
                    }}
                    pattern="\d*"
                    maxLength={10}
                  />
                  {errors.mobileNumber && (
                    <p className="error-message">{errors.mobileNumber}</p>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Email Address <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.email ? "input-error" : ""}`}
                    placeholder="Enter Email Address"
                    name="email"
                    value={userInfo.email}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <p className="error-message">{errors.email}</p>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Citizenship Number <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={14}
                    className={`form-control ${errors.citizenship ? "input-error" : ""}`}
                    placeholder="Enter Citizenship Number"
                    name="citizenship"
                    value={userInfo.citizenship}
                    onChange={handleChange}
                  />
                  {errors.citizenship && (
                    <p className="error-message">{errors.citizenship}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="dashboard-card mt-2">
              <h5 className="text-uppercase mb-4">User Details</h5>
              <div className="row g-4">
                <div className="col-md-4">
                  <label className="form-label">
                    Company Name <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    className={`form-control ${errors.companyName ? "input-error" : ""}`}
                    placeholder="Enter Company Name"
                    onChange={handleChange}
                    value={userInfo.companyName}
                  />
                  {errors.companyName && (
                    <p className="error-message">{errors.companyName}</p>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Department <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    name="department"
                    className={`form-control ${errors.department ? "input-error" : ""}`}
                    placeholder="Enter Department"
                    onChange={handleChange}
                    value={userInfo.department}
                  />
                  {errors.department && (
                    <p className="error-message">{errors.department}</p>
                  )}
                </div>
                <div className="col-md-4" data-field="position">
                  <label className="form-label">
                    Position <span className="primary-color">*</span>
                  </label>
                  <SelectWithError
                    styles={customSelectStyles}
                    isError={Boolean(errors.position)}
                    options={positionOptions}
                    placeholder="Select Position"
                    value={
                      positionOptions.find(
                        (opt) => opt.value === userInfo.position,
                      ) || null
                    }
                    onChange={(selected: SelectOption | null) =>
                      handleSelectChange("position", selected)
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {errors.position && (
                    <p className="error-message">{errors.position}</p>
                  )}
                </div>
                <div className="col-md-4" data-field="userRole">
                  <label className="form-label">
                    User Role <span className="primary-color">*</span>
                  </label>
                  <SelectWithError
                    styles={customSelectStyles}
                    isError={Boolean(errors.userRole)}
                    options={userRoleOptions}
                    placeholder="Select User Role"
                    value={
                      userRoleOptions.find(
                        (opt) => opt.value === userInfo.userRole,
                      ) || null
                    }
                    onChange={(selected: SelectOption | null) =>
                      handleSelectChange("userRole", selected)
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {errors.userRole && (
                    <p className="error-message">{errors.userRole}</p>
                  )}
                </div>
                <div className="col-md-4" data-field="userStatus">
                  <label className="form-label">
                    User Status <span className="primary-color">*</span>
                  </label>
                  <SelectWithError
                    styles={customSelectStyles}
                    isError={Boolean(errors.userStatus)}
                    options={userStatusOptions}
                    placeholder="Select User Status"
                    value={
                      userStatusOptions.find(
                        (opt) => opt.value === userInfo.userStatus,
                      ) || null
                    }
                    onChange={(selected: SelectOption | null) =>
                      handleSelectChange("userStatus", selected)
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {errors.userStatus && (
                    <p className="error-message">{errors.userStatus}</p>
                  )}
                </div>
                <div className="col-12">
                  <label className="form-label">
                    Upload Image (PDF, JPEG, PNG)
                  </label>

                  <div
                    className={`upload-box p-4 ${!userInfo?.file && !userInfo?.documentUrl && "text-center"}`}
                  >
                    {userInfo?.file ? (
                      renderFilePreview(
                        userInfo.file,
                        userInfo.fileName,
                        (e: any) => handleFileChange(e),
                      )
                    ) : userInfo?.documentUrl ? (
                      renderExistingFilePreview(
                        userInfo.documentUrl,
                        handleFileChange,
                      )
                    ) : (
                      <>
                        <Upload />

                        <p className="mb-1">
                          Choose a file or drag and drop it here.
                        </p>

                        <small className="d-block mb-3">
                          (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                        </small>

                        <label className="btn btn-badge text-main-color">
                          Browse File
                          <input
                            hidden
                            type="file"
                            onChange={(e) => {
                              handleFileChange(e);
                            }}
                          />
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`d-flex mt-4 justify-content-end ${isMobile ? "flex-wrap gap-2" : ""}`}
        >
          <div className="d-flex gap-2">
            <button className="btn px-3 btn-badge-border">Cancel</button>
            <button
              className="btn btn-badge-primary px-3"
              onClick={handleSubmit}
            >
              Save User
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditUser;
